import React, { useContext, useEffect, useState } from 'react'
import { FaTriangleExclamation, FaTruckFast } from 'react-icons/fa6'
import { Card, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import LogoSeattle from '@resources/imgs/seattle.png'
import LogoEuropean from '@resources/imgs/european.webp'
import LogoProssed from '@resources/imgs/logo-proseed.webp'
import Userphoto from '@resources/imgs/userphoto.svg'
import New from '@resources/imgs/icon-new.svg'
import Trompas from '@resources/imgs/tratament-3.png'
import Embriao from '@resources/imgs/tratament-1.png'
import Ovulos from '@resources/imgs/tratament-2.png'
import FlagUSA from '@resources/imgs/flags/usa.png'
import FlagEU from '@resources/imgs/flags/dinamarca.png'
import FlagBR from '@resources/imgs/flags/brazil.png'
import '@resources/fontes/fontes.css'
import '@resources/css/donators.css'
import '@resources/css/country-flag.css'
import Login from '@screens/Auth/Login/Login'
import IDonor from '@interfaces/IDonor'
import AuthContext from '@contexts/Auth'
import { displayOnProduction } from '@helpers/Utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { IoChevronDown } from 'react-icons/io5'
import { FaHeart, FaRegHeart, FaCheck, FaCamera, FaDna, FaInfoCircle } from 'react-icons/fa'
import { TbBabyCarriage } from 'react-icons/tb'

type Props = {
  donor?: IDonor
  handleFavorite: (id: string) => void
  cartDonorIds?: string[]
  addingToCart?: string[]
  setAddingToCart?: React.Dispatch<React.SetStateAction<string[]>>
}

// Global variable to track if the modal has been shown
let globalHasShownModal = false

const Donators: React.FC<Props> = ({ donor, handleFavorite, cartDonorIds = [], addingToCart = [], setAddingToCart }) => {
  const { user, onAddDonorToCart, activeInProgressOrders } = useContext(AuthContext)

  const [showModal, setShowModal] = useState(false)
  const donorId = donor?.internal_id ? String(donor.internal_id) : ''
  const [isInCart, setIsInCart] = useState<boolean>(() => donorId ? cartDonorIds.includes(donorId) : false)

  const [ searchParams ] = useSearchParams()

  const navigate = useNavigate()

  const isPatient = user?.roles?.some((role: any) => role.name === 'Paciente')

  useEffect(() => {
    if (!donorId) {
      setIsInCart(false)
      return
    }

    setIsInCart(cartDonorIds.includes(donorId))
  }, [cartDonorIds, donorId])

  useEffect(() => {
    if (searchParams.has('login') && searchParams.get('login') === '1' && !globalHasShownModal) {
      setShowModal(true)
      globalHasShownModal = true // Set the global variable to true after showing the modal
    }
  }, [searchParams])

  const handleShowModal = () => {
    if (user) {
      navigate(`/doadores/${donor?.internal_id?.toLowerCase()}`)
      window.scrollTo(0, 0)
    } else {
      setShowModal(true)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleAddToCart = () => {
    if (!donorId || isInCart || addingToCart.includes(donorId)) {
      return
    }

    setAddingToCart?.(prev => [...prev, donorId])

    onAddDonorToCart?.(donorId, (success) => {
      if (success) {
        setIsInCart(true)
      }
      setAddingToCart?.(prev => prev.filter(id => id !== donorId))
    })
  }

  const showContent = (e: any) => {
    e.target.closest('.show-less').classList.toggle('show-more')
  }

  const getCountryFlag = () => {
    if (donor?.sperm_bank?.name === 'Seattle Sperm Bank') {
      return FlagUSA
    } else if (donor?.sperm_bank?.name === 'European Sperm Bank') {
      return FlagEU
    } else {
      return FlagBR
    }
  }

  const cgtExomeTooltip = (
    <Tooltip id="cgt-exome-tooltip">
      O mapeamento genético do doador é só metade da informação necessária para análise de compatibilidade genética. Confira documento anexo para mais explicações do mapeamento genético!
    </Tooltip>
  )

  return (
    <>
      <Card className="donators flex-column flex-md-row mb-3">
        <Card.Header className="card-header-swiper d-flex justify-content-sm-center border-right-sm-0 p-1" id="card-swiper">
          <div className="left-side">
            <figure className="d-flex justify-content-center mb-3 mr-0">
              <img className="photo" src={donor?.avatar || Userphoto} alt="" />
              <figcaption className="">
                <div className="country-flag">
                  <img src={getCountryFlag()} alt="Country flag" />
                </div>
                {donor?.photos && donor?.photos.length > 0 ? (
                  <OverlayTrigger placement="top" overlay={<Tooltip id="photos-button">Esse doador possui fotos próprias.<br />Clique no <b>Perfil Completo</b> para visualizar.</Tooltip>}>
                    <div className="camera">
                      <FaCamera size={18} color="white" />
                    </div>
                  </OverlayTrigger>
                ) : null}
                <div className="like">
                  {user && donor ? (
                    <button className="btn-favorite" onClick={() => handleFavorite(donor.internal_id)}>
                      {donor.is_favorited ? (
                        <FaHeart size={22} color="#F93D6D" />
                      ) : (
                        <FaRegHeart size={22} color="white" />
                      )}
                    </button>
                  ) : null}
                </div>
                {donor?.is_new_donor ? (
                  <div className="icon-new">
                    <img src={New} alt="" />
                  </div>
                ) : null}
              </figcaption>
            </figure>
          </div>

          <div className="left-side-dados">
            <Card.Title className="card-title-swiper m-0">
              <div className="card-title-top d-flex align-items-start flex-column flex-md-row justify-content-between position-relative">
                <div id="dados">
                  <div className="id">ID: {donor?.internal_id}</div>
                  <div className="idade">
                    {donor?.nickname && (
                      `${donor?.nickname}`
                    )}
                    {((donor?.nickname && donor?.age) || (donor?.nickname && !donor?.age && donor?.sperm_bank?.name !== 'Pro-Seed' && donor?.donor_type !== null)) && (
                      <br />
                    )}
                    {donor?.age && (
                      `${donor?.age} anos`
                    )}
                    {donor?.age && donor?.sperm_bank?.name !== 'Pro-Seed' && donor?.donor_type !== null && (
                      <br />
                    )}
                    {donor?.sperm_bank?.name !== 'Pro-Seed' && donor?.donor_type !== null && (
                      `${donor?.donor_type}`
                    )}
                  </div>
                </div>
                <figure className={donor?.sperm_bank?.name === 'Pro-Seed' ? 'sperm-bank-proseed' : ''}>
                  {donor?.sperm_bank?.name === 'Pro-Seed' ? (
                    <img src={LogoProssed} className="sperm-bank-proseed" alt="" />
                  ) : donor?.sperm_bank?.name === 'European Sperm Bank' ? (
                    <img src={LogoEuropean} className="sperm-bank-european" alt="" />
                  ) : donor?.sperm_bank?.name === 'Seattle Sperm Bank' ? (
                    <img src={LogoSeattle} className="sperm-bank-seattle" alt="" />
                  ) : (
                    <img src={LogoProssed} className="sperm-bank-proseed" alt="" />
                  )}
                </figure>
              </div>
              <div className="card-title-bottom">
                <div className="profissao" title={donor?.occupation}>{donor?.occupation?.length > 35 ? `${donor?.occupation.slice(0, 35)}...` : donor?.occupation}</div>
                <p className="text-dentro m-0">
                  {donor?.proseed_description?.length > 180 ? `${donor?.proseed_description.slice(0, 180)}...` : donor?.proseed_description}
                </p>
              </div>
            </Card.Title>

          </div>
        </Card.Header>

        <Card.Body className="card-body-swiper">
          <p className="text-fora col-12 d-none m-0">
            {donor?.proseed_description}
          </p>
          <div className="show-less row m-0 mb-4">
            <button className="show-more-btn"><IoChevronDown size={30} onClick={showContent} /></button>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Cor da Pele
              </div>
              <div className="result d-flex">
                <span>{donor?.skin_color?.name}</span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Cor dos Olhos
              </div>
              <div className="result d-flex">
                <span>{donor?.eye_color?.name}</span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Cor do Cabelo
              </div>
              <div className="result d-flex">
                <span>{donor?.hair_color?.name}</span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Tipo de Cabelo
              </div>
              <div className="result d-flex">
                <span>{donor?.hair_type?.name}</span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Raça
              </div>
              <div className="result d-flex">
                <span>{donor?.race?.name}</span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Tipo Sanguíneo
              </div>
              <div className="result d-flex">
                <span>{donor?.blood_group?.name}</span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Altura
              </div>
              <div className="result d-flex">
                <span>{donor?.height}</span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3 custom-grid">
              <div className="title">
              Peso
              </div>
              <div className="result d-flex">
                <span>{donor?.weight}Kg</span>
              </div>
            </div>
          </div>
          {donor?.cgt_exome ? (
            <div className="col-12 mt-2 d-flex align-items-center gap-5">
              <FaDna size="20" color="#FA5A84" />
              {!donor?.mapped_genes ? (
                <p className="mb-0 text-left">
                  Mapeamento Genético: <br className="d-md-none" /><b>{donor?.cgt_exome}</b>
                </p>
              ) : (
                <p className="mb-0 text-left">
                  <b>Painel Genético Expandido <br className="d-md-none" />({donor?.mapped_genes} Genes)</b>
                </p>
              )}
              <OverlayTrigger placement="top" overlay={cgtExomeTooltip}>
                <span><FaInfoCircle /></span>
              </OverlayTrigger>
            </div>
          ) : null}
          <div className="col-12 mt-2 mb-2 d-flex align-items-center gap-5">
            <FaTruckFast size="20" color="#FA5A84" />
            <p className="mb-0 text-left">
              Disponibilidade para <b>Envio imediato (48h)</b>
              {(donor?.total_available_straws ?? 99) <= 2 && (
                <span className="low-stock-badge ml-0 ml-xl-2">
                  <FaTriangleExclamation /> Estoque Baixo
                </span>
              )}
            </p>
          </div>
          <div className="row m-0">
            <div className="col-12">
              <div className="content">
                <div className="m-0 row align-items-center">
                  <div className="col-12 responsivecol6 col-lg-6 pl-0 text-left">
                    <div className="envio d-none">
                      <span>Disponível para tratamento de:</span>
                    </div>
                    {donor?.available_treatment_names && donor?.available_treatment_names?.length > 0 ? (
                      <div className="tratamentos d-flex pt-2 gap-5">
                        {donor?.available_treatment_names.includes('ICSI') && (
                          <div className="result d-flex">
                            <img src={Ovulos} alt="" />
                            <span>ICSI</span>
                          </div>
                        )}
                        {donor?.available_treatment_names.includes('FIV') && (
                          <div className="result d-flex">
                            <img src={Embriao} alt="" />
                            <span>FIV</span>
                          </div>
                        )}
                        {donor?.available_treatment_names.includes('IIU') && (
                          <div className="result d-flex">
                            <img src={Trompas} alt="" />
                            <span>Inseminação</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="tratamentos d-flex">
                        <div className="result d-flex">
                          <span>Não disponível</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col-12 responsivecol6 buttons-area col-lg-6 pt-2 pr-0">
                    <div className="button d-flex align-items-center justify-content-between w-100">
                      <button className="order-now btn btn-medium-primary" onClick={handleShowModal}>
                        Perfil Completo
                      </button>

                      {isPatient && displayOnProduction() && activeInProgressOrders.length < 1 && (
                        <button
                          className={`btn btn-medium-primary cart ${isInCart ? 'in-cart' : ''}`}
                          onClick={handleAddToCart}
                          disabled={isInCart || addingToCart.includes(donorId)}
                          title={isInCart ? 'Já no carrinho' : addingToCart.includes(donorId) ? 'Adicionando...' : 'Adicionar ao carrinho'}
                        >
                          <figure>
                            {isInCart ? (
                              <FaCheck className="cart-icon"/>
                            ) : addingToCart.includes(donorId) ? (
                              <div className="spinner-border spinner-border-sm cart-icon" role="status"></div>
                            ) : (
                              <TbBabyCarriage className="cart-icon"/>
                            )}
                          </figure>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="xl">
        <Modal.Header className="border-0">
          <button type="button" className="close" onClick={handleCloseModal}>
            <span aria-hidden="true">×</span>
          </button>
        </Modal.Header>

        <Modal.Body>
          <Login donorId={donor?.internal_id} />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default Donators
